// src/utils/invoiceGenerator.js
import { db, storage } from "../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import html2pdf from "html2pdf.js";

// Function to fetch the conversion rate from ExchangeRate-API
export const fetchConversionRate = async (fromCurrency, toCurrency) => {
  const response = await fetch(
    `https://api.exchangerate-api.com/v4/latest/${fromCurrency}`
  );
  const data = await response.json();
  return data.rates[toCurrency];
};

// Function to fetch GBP to AED conversion rate from CBUAE (Central Bank of UAE)
export const fetchCBUAEConversionRate = async () => {
  try {
    // Fetch the HTML page directly from CBUAE
    const cbuaeUrl =
      "https://www.centralbank.ae/umbraco/Surface/ExchangeHome/GetExchangeRateData";

    const response = await fetch(cbuaeUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();

    // Extract GBP rate using regex - matches: GBP <b>4.830622</b>
    const gbpRegex = /GBP\s*<b>([\d.]+)<\/b>/i;
    const match = html.match(gbpRegex);

    if (match && match[1]) {
      const rate = parseFloat(match[1]);
      console.log("CBUAE GBP→AED rate:", rate);
      return rate;
    }

    throw new Error("GBP rate not found in CBUAE HTML");
  } catch (error) {
    console.error("Error fetching CBUAE rate:", error);
    // Fallback to ExchangeRate-API if CBUAE fails
    console.warn("Falling back to ExchangeRate-API");
    return await fetchConversionRate("GBP", "AED");
  }
};

const assetPath = (fileName) => `${window.location.origin}/${fileName}`;
const toNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};
const toMoney = (val) =>
  Math.round((toNumber(val) + Number.EPSILON) * 100) / 100;

export const generateInvoice = async ({
  projectId,
  invoiceChoice,
  selectedCompany,
  formatMoney,
  formatMoneyWithoutPoundSign,
  useManualAED = false,
  showGBP = true,
  exchangeRateSource = "ExchangeRate-API", // Default to ExchangeRate-API, options: "ExchangeRate-API", "CBUAE"
}) => {
  const projectDoc = await getDoc(doc(db, "projects", projectId));

  if (projectDoc.exists()) {
    const projectData = projectDoc.data();

    // Fetch the client details from the clients collection
    const clientsCollection = collection(db, "clients");
    const clientQuery = query(
      clientsCollection,
      where("clientName", "==", projectData.client)
    );
    const clientSnapshot = await getDocs(clientQuery);
    let clientData = {};

    if (!clientSnapshot.empty) {
      clientData = clientSnapshot.docs[0].data();
    } else {
      console.error("No matching client document!");
    }

    // Construct the client address
    const addressFields = [
      clientData.addressLine1,
      clientData.addressLine2,
      clientData.addressLine3,
      clientData.city,
      clientData.postCode,
      clientData.country,
    ];
    const clientAddress = addressFields.filter((field) => field).join("<br>");

    // Format the date
    const rawDate = projectData.dateForInvoice;
    const dateObject = new Date(rawDate);
    const formattedDate = dateObject.toLocaleDateString("en-GB"); // This will format the date as DD/MM/YYYY

    const tasks = projectData.tasks || [];
    const fallbackVatRate = toNumber(projectData.vat);
    const taskIncomeSum = toMoney(
      tasks.reduce((acc, t) => acc + toNumber(t.income), 0)
    );
    const totalAmount =
      taskIncomeSum > 0 ? taskIncomeSum : toMoney(projectData.income);
    const vatFromTasks = toMoney(
      tasks.reduce((acc, t) => {
        const rate = toNumber(t.vat);
        if (rate <= 0) return acc;
        return acc + toNumber(t.income) * (rate / 100);
      }, 0)
    );
    const vatAmount =
      vatFromTasks || toMoney(totalAmount * (fallbackVatRate / 100));
    const vatApplied = vatAmount > 0;
    const totalIncVat = toMoney(totalAmount + vatAmount);
    const labelVatRate =
      vatFromTasks && tasks.some((t) => toNumber(t.vat) > 0)
        ? toNumber(tasks.find((t) => toNumber(t.vat) > 0)?.vat)
        : fallbackVatRate;

    // Format the VAT label and amount
    const vatLabel = labelVatRate > 0 ? `VAT @ ${labelVatRate}%` : "VAT";
    const formattedVatAmount = `${formatMoney(vatAmount)}`;

    // Fetch the invoice template from the public folder
    const invoiceTemplateResponse = await fetch("/invoiceTemplate.html?v=1.6");
    let invoiceTemplate = await invoiceTemplateResponse.text();

    // Fetch conversion rate from GBP to AED based on selected source
    let conversionRate;
    if (exchangeRateSource === "CBUAE") {
      conversionRate = await fetchCBUAEConversionRate();
    } else {
      // Default to ExchangeRate-API
      conversionRate = await fetchConversionRate("GBP", "AED");
    }

    // Capture the CBUAE rate for invoice notes when GBP is displayed on UAE invoices
    let cbuaeRateForNote = null;
    if (selectedCompany === "UAE" && showGBP) {
      cbuaeRateForNote =
        exchangeRateSource === "CBUAE"
          ? conversionRate
          : await fetchCBUAEConversionRate();
    }

    // Define company details based on the selected company
    const companyDetails = {};

    if (selectedCompany === "UK") {
      companyDetails.name = "Fort Corps Limited";
      companyDetails.addressLines = [
        "Belsyre Court",
        "First Floor, 57 Woodstock Road",
        "Oxford, England, OX2 6HJ",
      ];
      companyDetails.phone = "+44 186 523 8285";
      companyDetails.email = "zak@fortcorps.com";
      companyDetails.accountHolder = "Fort Corps Limited";
      companyDetails.bankDetailsLines = [
        "Barclays Bank, 54 Cornmarket Street, Oxford, OX1 3HB",
      ];
      companyDetails.sortCode = "20-65-20";
      companyDetails.accountNo = "23954005";
      companyDetails.iban = "GB56BUKB20652023954005";
      companyDetails.swift = "BUKBGB22";
      companyDetails.termsOfPayment = "30 days";
      companyDetails.footerDetails =
        "Fort Corps Limited | Company Number 13497195 | VAT Registration Number 404510152 | Belsyre Court, First Floor, 57 Woodstock Road, Oxford, England, OX2 6HJ";
      companyDetails.logo = assetPath("invoice_logo.png");
    } else if (selectedCompany === "UAE") {
      companyDetails.name = "Fort Core LLC FZ";
      companyDetails.addressLines = [
        "Meydan Grandstand, 6th Floor",
        "Meydan Road, Nad Al Sheba",
        "Dubai, United Arab Emirates",
      ];

      companyDetails.phone = "+971 4 368 7580";
      companyDetails.email = "zak@fortcore.com";
      companyDetails.trn = "104247542400003";
      companyDetails.accountHolder = "Fort Core LLC FZ";
      companyDetails.bankDetailsLines = [
        "Abu Dhabi Islamic Bank, Arenco Tower, Dubai Media City",
      ];
      companyDetails.sortCode = "N/A";
      companyDetails.accountNo = "19186681";
      companyDetails.iban = "AE210500000000019186681";
      companyDetails.swift = "ABDIAEADXXX";
      companyDetails.termsOfPayment = "30 days";
      companyDetails.footerDetails =
        "Fort Core LLC FZ | Tax Reg Number 104247542400003 | VAT Reg Number 103000956680 | Meydan Grandstand, 6th Floor, Meydan Road, Nad Al Sheba, Dubai, United Arab Emirates";
      companyDetails.logo = vatApplied
        ? assetPath("uae_logo_tax_invoice.png")
        : assetPath("uae_logo_invoice.png");
    }

    // If UAE company is selected, show the Fee (AED) column and header
    if (selectedCompany === "UAE") {
      let totalAed, vatAedAmount, totalIncVatAed;

      if (useManualAED) {
        totalAed = toMoney(
          tasks.reduce((acc, task) => acc + toNumber(task.incomeAED), 0)
        );

        const vatAedFromTasks = toMoney(
          tasks.reduce((acc, task) => {
            const rate =
              toNumber(task.vatAED ?? task.vat ?? fallbackVatRate) || 0;
            if (rate <= 0) return acc;
            return acc + toNumber(task.incomeAED) * (rate / 100);
          }, 0)
        );

        const manualIncVatSum = toMoney(
          tasks.reduce(
            (acc, task) => acc + toNumber(task.amountIncVatAED),
            0
          )
        );

        totalIncVatAed =
          manualIncVatSum > 0
            ? manualIncVatSum
            : toMoney(totalAed + vatAedFromTasks);
        vatAedAmount =
          vatAedFromTasks > 0
            ? vatAedFromTasks
            : toMoney(totalIncVatAed - totalAed);
      } else {
        totalAed = toMoney(totalAmount * conversionRate);
        vatAedAmount = toMoney(vatAmount * conversionRate);
        totalIncVatAed = toMoney(totalAed + vatAedAmount);
      }

      // Update the template to show the AED columns for total, VAT, and total inc VAT
      invoiceTemplate = invoiceTemplate.replace(
        'id="feeAEDHeader" style="display:none;"',
        'id="feeAEDHeader" style="display:table-cell;"'
      );
      invoiceTemplate = invoiceTemplate.replace(
        'id="totalAED" style="display:none;"',
        'id="totalAED" style="display:table-cell;"'
      );
      invoiceTemplate = invoiceTemplate.replace(
        'id="vatAEDAmount" style="display:none;"',
        'id="vatAEDAmount" style="display:table-cell;"'
      );
      invoiceTemplate = invoiceTemplate.replace(
        'id="totalIncVatAED" style="display:none;"',
        'id="totalIncVatAED" style="display:table-cell;"'
      );

      // Apply the smaller font size for UAE
      invoiceTemplate = invoiceTemplate.replace(
        'class="invoice-items"',
        'class="invoice-items uae-font-small"'
      );

      // Hide GBP columns if showGBP is false
      if (!showGBP) {
        invoiceTemplate = invoiceTemplate
          .replace(
            'id="feeGBPHeader"',
            'id="feeGBPHeader" style="display:none;"'
          )
          .replace('id="total"', 'id="total" style="display:none;"')
          .replace('id="vatAmount"', 'id="vatAmount" style="display:none;"')
          .replace(
            'id="totalIncVat"',
            'id="totalIncVat" style="display:none;"'
          );
      } else {
        // Remove the UK-specific sort code row when GBP is shown on UAE invoices
        invoiceTemplate = invoiceTemplate.replace(
          /\s*<p>Sort Code: \[Sort Code\]<\/p>\s*\n?/,
          ""
        );

        const numericCbuaeRate = Number(cbuaeRateForNote);
        const formattedCbuaeRate = Number.isFinite(numericCbuaeRate)
          ? numericCbuaeRate.toFixed(5)
          : null;

        if (formattedCbuaeRate) {
          const cbuaeNote = `Note: A Central Bank of the U.A.E. rate of ${formattedCbuaeRate} AED per GBP was applied at the time this invoice was generated. Any queries about this invoice should be raised within seven days of its receipt.`;

          invoiceTemplate = invoiceTemplate.replace(
            /<p>Note: Any queries about this invoice should be raised within seven days of its receipt\.<\/p>/,
            `<p>${cbuaeNote}</p>`
          );
        }
      }

      // Replace placeholders with AED values
      invoiceTemplate = invoiceTemplate
        .replace(
          "[Total AED]",
          ` ${formatMoneyWithoutPoundSign(totalAed).replace(".00", "")}`
        )
        .replace(
          "[VAT AED]",
          ` ${formatMoneyWithoutPoundSign(vatAedAmount).replace(".00", "")}`
        )
        .replace(
          "[Total Inc. VAT AED]",
          `${formatMoneyWithoutPoundSign(totalIncVatAed).replace(".00", "")}`
        );

      // Remove the Tel line and add TRN line for UAE
      invoiceTemplate = invoiceTemplate.replace(
        /<p>Tel: \[Company Phone\]<\/p>\s*\n\s*<p>Email: <a href="mailto:\[Company Email\]">\[Company Email\]<\/a><\/p>/,
        '<p>Email: <a href="mailto:[Company Email]">[Company Email]</a></p>\n                    <p><strong>TRN:</strong> [Company TRN]</p>'
      );

      // Replace the footer structure for UAE to have a single line format
      const uaeFooterReplacement = `<div class="footer">
            <p>[Footer Company Details] | Tel: [Company Phone] | Email: [Company Email]</p>
        </div>`;

      invoiceTemplate = invoiceTemplate.replace(
        /<div class="footer">[\s\S]*?<\/div>/,
        uaeFooterReplacement
      );
    }

    // Replace placeholders with actual data
    invoiceTemplate = invoiceTemplate
      .replace("[Client Name]", `<b>${clientData.clientName || ""}</b>`)
      .replace("[Client Address]", clientAddress)
      .replace("[Invoice Date]", formattedDate.replace(/\//g, "-")) // Replace slashes with dashes
      .replace("[Invoice No]", projectData.invoiceNo)
      .replace("[Project No]", projectData.projectRef)
      .replace("[Project Ref]", projectData.paymentNotes)
      .replace("[Ordered by]", projectData.poc)
      .replace("[Total]", `${formatMoney(totalAmount).replace(".00", "")}`)
      .replace("VAT", vatLabel) // Replace the VAT label with VAT @ 20%, etc.
      .replace("[VAT Amount]", formattedVatAmount) // Replace the VAT amount
      .replace(
        "[Total Inc. VAT]",
        `${formatMoney(totalIncVat).replace(".00", "")}`
      )
      .replace(/\[Company Name\]/g, companyDetails.name)
      .replace(
        /\[Company Address Line 1\]/g,
        companyDetails.addressLines[0] || ""
      )
      .replace(
        /\[Company Address Line 2\]/g,
        companyDetails.addressLines[1] || ""
      )
      .replace(
        /\[Company Address Line 3\]/g,
        companyDetails.addressLines[2] || ""
      )
      .replace(
        /\[Company Address Line 4\]/g,
        companyDetails.addressLines[3] || ""
      )
      .replace(/\[Company Phone\]/g, companyDetails.phone)
      .replace(/\[Company Email\]/g, companyDetails.email)
      .replace(/\[Company TRN\]/g, companyDetails.trn || "")
      .replace(/\[Account Holder\]/g, companyDetails.accountHolder)
      .replace(
        /\[Bank Details Line 1\]/g,
        companyDetails.bankDetailsLines[0] || ""
      )
      .replace(
        /\[Bank Details Line 2\]/g,
        companyDetails.bankDetailsLines[1] || ""
      )
      .replace(/\[Sort Code\]/g, companyDetails.sortCode)
      .replace(/\[Account No\]/g, companyDetails.accountNo)
      .replace(/\[IBAN\]/g, companyDetails.iban)
      .replace(/\[SWIFT\]/g, companyDetails.swift)
      .replace(/\[Terms of Payment\]/g, companyDetails.termsOfPayment)
      .replace(/\[Footer Company Details\]/g, companyDetails.footerDetails)
      .replace(/\[Company Logo\]/g, companyDetails.logo);

    // Task description mapping
    const taskDescriptionMap = {
      "DSS": "Director-Shareholder Search",
      "SR": "Screening Report",
      "BC": "Background Checks",
      "PS": "Property Ownership Enquiries",
      "VS": "Vehicle Ownership Enquiries",
      "IMEQ": "Immigration Enquiries",
      "BEQ": "Banking Relationship Enquiries",
      "FL": "Corporate Records Report",
      "SS": "Subsidiary Search",
      "EQ": "Human Source Enquiries",
      "LC": "Litigation Enquiries",
      "MA": "Media Archive Search",
      "RPS": "Reverse Property Ownership Enquiries",
      "SV": "Site Visit",
      "VES": "Vessle Ownership Enquiries",
    };

    // Populate the task details in the invoice
    let tasksHtml = "";
    tasks.forEach((task, index) => {
      const subject =
        invoiceChoice === "Anonymise subject"
          ? "Subject of Interest"
          : task.subject;
      const taskDescription = taskDescriptionMap[task.task]
        ? `${taskDescriptionMap[task.task]} (${task.country})`
        : `${task.task} (${task.country})`;

      // Determine which AED value to use
      const feeAED =
        useManualAED && task.incomeAED
          ? parseFloat(task.incomeAED).toFixed(2)
          : (task.income * conversionRate).toFixed(2);

      tasksHtml += `
        <tr>
        <td>${index + 1}</td>
        <td>${subject}</td>
        <td>${taskDescription}</td>
        ${showGBP ? `<td>${formatMoney(task.income)}</td>` : ""}
        ${
          selectedCompany === "UAE"
            ? `<td> ${formatMoneyWithoutPoundSign(feeAED).replace(
                ".00",
                ""
              )}</td>`
            : ""
        }
        </tr>
      `;
    });

    // Insert the tasksHtml into the invoiceItems tbody
    invoiceTemplate = invoiceTemplate.replace(
      '<tbody id="invoiceItems">',
      `<tbody id="invoiceItems">${tasksHtml}`
    );

    // Calculate the number of rows and adjust the footer and payment details margin-top accordingly
    const rowCount = tasks.length;

    let footerMarginTop = 100; // default margin
    let paymentMarginTop = 100; // default margin

    if (rowCount >= 16) {
      footerMarginTop = 750;
      paymentMarginTop = 0;
    } else if (rowCount == 15) {
      footerMarginTop = 700;
      paymentMarginTop = 80;
    } else if (rowCount == 14) {
      footerMarginTop = 650;
      paymentMarginTop = 650;
    } else if (rowCount == 13) {
      footerMarginTop = 700;
      paymentMarginTop = 160;
    } else if (rowCount == 12) {
      footerMarginTop = 550;
      paymentMarginTop = 550;
    } else if (rowCount == 11) {
      footerMarginTop = 500;
      paymentMarginTop = 500;
    } else if (rowCount == 10) {
      footerMarginTop = 700;
      paymentMarginTop = 250;
    } else if (rowCount == 9) {
      footerMarginTop = 700;
      paymentMarginTop = 300;
    } else if (rowCount == 8) {
      footerMarginTop = 700;
      paymentMarginTop = 300;
    } else if (rowCount == 7) {
      footerMarginTop = 700;
      paymentMarginTop = 300;
    } else if (rowCount == 6) {
      footerMarginTop = 20;
      paymentMarginTop = 15;
    } else if (rowCount == 5) {
      footerMarginTop = 20;
      paymentMarginTop = 20;
    } else if (rowCount == 4) {
      footerMarginTop = 30;
      paymentMarginTop = 20;
    } else if (rowCount == 3) {
      footerMarginTop = 40;
      paymentMarginTop = 40;
    } else if (rowCount == 2) {
      footerMarginTop = 50;
      paymentMarginTop = 50;
    } else {
      footerMarginTop = 105;
      paymentMarginTop = 20; // Adjust as necessary for fewer rows
    }

    // Update the footer and payment details margin-top in the template
    invoiceTemplate = invoiceTemplate.replace(
      "margin-top: 100px;",
      `margin-top: ${footerMarginTop}px;`
    );
    invoiceTemplate = invoiceTemplate.replace(
      "[PAYMENT_MARGIN_TOP]",
      `${paymentMarginTop}px`
    );

    // Create a hidden div in memory to hold the invoice template
    const invoiceDiv = document.createElement("div");
    invoiceDiv.innerHTML = invoiceTemplate;

    // Generate PDF using html2pdf.js with higher quality settings
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `${projectData.invoiceNo}.pdf`,
      image: { type: "jpeg", quality: 1.0 },
      html2canvas: {
        scale: 4,
        useCORS: true,
        allowTaint: true,
        logging: true,
        dpi: 300,
        letterRendering: true,
      },
      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "portrait",
      },
    };

    const pdfBlob = await html2pdf().from(invoiceDiv).set(opt).outputPdf("blob");

    const timestamp = Date.now();
    const storagePath = `invoices/${projectId}/${timestamp}-${projectData.invoiceNo || projectData.projectRef || "invoice"}.pdf`;
    const storageRef = ref(storage, storagePath);

    const metadata = {
      contentType: "application/pdf",
      customMetadata: {
        projectId,
        projectRef: projectData.projectRef || "",
        invoiceNo: projectData.invoiceNo || "",
        selectedCompany,
        exchangeRateSource,
        showGBP: String(showGBP),
        useManualAED: String(useManualAED),
      },
    };

    const uploadTask = uploadBytesResumable(storageRef, pdfBlob, metadata);

    await new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        null,
        (error) => reject(error),
        () => resolve()
      );
    });

    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

    const invoiceRecord = {
      name: `${projectData.invoiceNo || projectData.projectRef || "invoice"}.pdf`,
      url: downloadURL,
      path: storagePath,
      size: pdfBlob.size,
      contentType: "application/pdf",
      createdAt: new Date(),
      selectedCompany,
      exchangeRateSource,
      showGBP,
      useManualAED,
      vatRate: fallbackVatRate || null,
      amountIncVat: totalIncVat || null,
    };

    const projectRef = doc(db, "projects", projectId);
    const existingData = projectDoc.data();
    const existingInvoices = existingData.invoices || [];
    const updatedInvoices = [...existingInvoices, invoiceRecord];

    await updateDoc(projectRef, { invoices: updatedInvoices });

    // Trigger a download for the user as well
    const downloadUrl = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = invoiceRecord.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  } else {
    console.error("No such project document!");
  }
};
